import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store'
import { ApiKeysServiceAPI } from '@API/data-services/api-keys-service'
import {
  AuthScopes,
  BackOfficeApiKeyCreateResponse,
  BackOfficeApiKeyRecord,
} from '@Generated/back-office-client/src'
import { formatDistanceToNow } from 'date-fns'
import { MagnifyingGlassIcon, Pencil1Icon } from '@radix-ui/react-icons'
import {
  Badge,
  Button,
  Callout,
  Flex,
  IconButton,
  Skeleton,
  Table,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Routes } from '@Components/main-app/types'
import { LoadingRows } from '@Components/util/loading-rows'
import { DeleteKeyAlert, KeyRevealModal, RotateKeyAlert } from './api-key-dialogs'

function formatLastUsed(lastUsedAt: string | null | undefined): string {
  if (!lastUsedAt) {
    return 'Never'
  }
  return formatDistanceToNow(new Date(lastUsedAt), { addSuffix: true })
}

export const ApiKeysList = () => {
  const { currentUser, activeCustomerId } = useSelector((state: RootState) => state.users)
  const [lookupValue, setLookupValue] = useState('')
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [highlightedKeyId, setHighlightedKeyId] = useState<string | null>(null)
  const [rotatedKeyResponse, setRotatedKeyResponse] =
    useState<BackOfficeApiKeyCreateResponse | null>(null)

  const canWrite = currentUser?.scopes.includes(AuthScopes.writeapi_keys)

  const {
    data: apiKeys,
    refetch,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [`${activeCustomerId}-api-keys`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: () => ApiKeysServiceAPI.listAll(),
  })

  const [mutationError, setMutationError] = useState<string | null>(null)

  const { mutate: deleteKey } = useMutation({
    mutationFn: ({ keyId, service }: { keyId: string; service: string }) => {
      return ApiKeysServiceAPI.deleteKey(keyId, service as BackOfficeApiKeyRecord['service'])
    },
    onSuccess: () => {
      setMutationError(null)
      refetch()
    },
    onError: () => {
      setMutationError('Failed to delete API key')
    },
  })

  const { mutate: rotateKey } = useMutation({
    mutationFn: ({ keyId, service }: { keyId: string; service: string }) => {
      return ApiKeysServiceAPI.rotateKey(keyId, service as BackOfficeApiKeyRecord['service'])
    },
    onSuccess: (response) => {
      setMutationError(null)
      setRotatedKeyResponse(response)
      refetch()
    },
    onError: () => {
      setMutationError('Failed to rotate API key')
    },
  })

  const { mutate: lookupKey, isPending: isLookupPending } = useMutation({
    mutationFn: (encodedKey: string) => {
      return ApiKeysServiceAPI.lookupKey(encodedKey)
    },
    onSuccess: (result) => {
      setLookupError(null)
      setHighlightedKeyId(result.id)
    },
    onError: () => {
      setLookupError('API key not found or invalid')
      setHighlightedKeyId(null)
    },
  })

  const handleLookup = () => {
    if (!lookupValue.trim()) {
      return
    }
    lookupKey(lookupValue.trim())
  }

  const rows = apiKeys?.map((key) => {
    const isHighlighted = highlightedKeyId === key.id

    return (
      <Table.Row
        key={key.id}
        style={isHighlighted ? { backgroundColor: 'var(--accent-3)' } : undefined}>
        <Table.RowHeaderCell>{key.description}</Table.RowHeaderCell>
        <Table.Cell>
          <Badge variant="surface">{key.service}</Badge>
        </Table.Cell>
        <Table.Cell>
          <Flex gap="1" wrap="wrap">
            {key.fullAccess ? (
              <Badge color="blue" variant="solid" size="1">
                Full Access
              </Badge>
            ) : (
              key.scopes.map((scope) => (
                <Badge key={scope} variant="soft" size="1">
                  {scope}
                </Badge>
              ))
            )}
          </Flex>
        </Table.Cell>
        <Table.Cell>
          <Badge color={key.isActive ? 'green' : 'red'} variant="soft">
            {key.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </Table.Cell>
        <Table.Cell>
          {key.legacy ? (
            <Badge color="orange" variant="soft">
              Legacy
            </Badge>
          ) : null}
        </Table.Cell>
        <Table.Cell>{formatLastUsed(key.lastUsedAt)}</Table.Cell>
        {canWrite ? (
          <Table.Cell>
            <Flex gap="2" justify="center">
              <Link to={Routes.EDIT_API_KEY.replace(':keyId', key.id)} state={key}>
                <IconButton variant="ghost" style={{ cursor: 'pointer' }}>
                  <Pencil1Icon />
                </IconButton>
              </Link>

              {!key.legacy ? (
                <RotateKeyAlert
                  description={key.description}
                  onRotate={() => rotateKey({ keyId: key.id, service: key.service })}
                />
              ) : null}

              <DeleteKeyAlert
                description={key.description}
                onDelete={() => deleteKey({ keyId: key.id, service: key.service })}
              />
            </Flex>
          </Table.Cell>
        ) : null}
      </Table.Row>
    )
  })

  return (
    <Flex justify="center" direction="column" gap="5">
      <Flex justify="between" align="end" gap="3">
        <Flex gap="2" align="end" style={{ flex: 1 }}>
          <TextField.Root
            placeholder="Paste an encoded API key to look it up..."
            value={lookupValue}
            onChange={(e) => setLookupValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleLookup()
              }
            }}
            style={{ flex: 1 }}>
            <TextField.Slot>
              <MagnifyingGlassIcon />
            </TextField.Slot>
          </TextField.Root>
          <Button
            variant="soft"
            onClick={handleLookup}
            loading={isLookupPending}
            style={{ cursor: 'pointer' }}>
            Lookup
          </Button>
        </Flex>

        {canWrite ? (
          <Link to={Routes.CREATE_API_KEY}>
            <Button style={{ cursor: 'pointer' }}>Create API Key</Button>
          </Link>
        ) : null}
      </Flex>

      {lookupError ? (
        <Callout.Root color="red">
          <Callout.Text>{lookupError}</Callout.Text>
        </Callout.Root>
      ) : null}

      {mutationError ? (
        <Callout.Root color="red">
          <Callout.Text>{mutationError}</Callout.Text>
        </Callout.Root>
      ) : null}

      {isError ? <Flex justify="center">Error loading API keys</Flex> : null}

      {isError ? null : (
        <Skeleton loading={isLoading}>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Service</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Scopes</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Legacy</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last Used</Table.ColumnHeaderCell>
                {canWrite ? <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell> : null}
              </Table.Row>
            </Table.Header>

            <Table.Body>{isLoading ? <LoadingRows /> : rows}</Table.Body>
          </Table.Root>
        </Skeleton>
      )}

      {rotatedKeyResponse ? (
        <KeyRevealModal
          title="Key Rotated Successfully"
          encodedKey={rotatedKeyResponse.encodedKey}
          onDismiss={() => setRotatedKeyResponse(null)}
        />
      ) : null}
    </Flex>
  )
}
